import React from 'react';
import R from 'ramda';
import { connect } from 'react-redux';
import { firstError } from 'common/validation';
import { getStep } from 'common/selectors';
import * as steps from 'common/steps';
import { SHOW_CANCEL_SUBMISSION_DIALOG } from 'submission/submissionActions';

import {
  ADD_COMMITTEE_MEMBER,
  COMPLETE_STEP,
  DEGREE_CHANGED,
  DEPARTMENT_CHANGED,
  FIELD_BLURRED,
  FIELD_CHANGED,
  LANGUAGE_CHANGED,
  LICENSE_TYPE_CHANGED,
} from 'dissertation-info/dissertationInfoActions';

import { getErrors, getFields, getLoading } from 'common/stepReducerUtil';

import {
  getDegrees,
  getDepartments,
  getLicenseTypes,
} from 'dissertation-info/dissertationInfoReducer';

import { Button } from 'react-toolbox/lib/button';
import { Dropdown } from 'react-toolbox/lib/dropdown';
import { Input } from 'react-toolbox/lib/input';
import LoadingPanel from 'general-components/LoadingPanel';
import StepPanelButtonBar from 'general-components/StepPanelButtonBar';
import DissertationInfoCategories from './DissertationInfoCategories';
import DissertationInfoLanguageAutocomplete from './DissertationInfoLanguageAutocomplete';
import styles from './DissertationInfo.css';
import DropdownTheme from 'shared-styles/DropdownTheme.css';

const MAX_COMMITTEE_MEMBERS = 10;

const mapLicenseTypes = R.memoize(
  R.pipe(
    R.reject(({ licenseTypeName }) => {
      const name = licenseTypeName.toLowerCase();
      return name.includes('public') && name.includes('domain');
    }),
    R.map(({ licenseTypeId, licenseTypeName }) => ({
      value: licenseTypeId,
      label: licenseTypeName,
    })),
  ),
);

const mapDegrees = R.memoize(
  R.map(({ degreeId, degreeName }) => ({ value: degreeId, label: degreeName })),
);

const mapDepartments = R.memoize(
  R.map(({ departmentId, departmentName }) => ({
    value: departmentId,
    label: departmentName,
  })),
);

const createCommitteeMemberRows = R.memoize((onFieldChange, errors, members) =>
  R.addIndex(R.map)(
    (member, index) => (
      <div key={`committee-member-${index}`} className={styles.row}>
        <Input
          type="text"
          label="First name"
          value={member.firstName}
          onChange={R.partial(onFieldChange, [
            ['committeeMembers', index, 'firstName'],
          ])}
          error={firstError(errors, `committeeMember[${index}].firstName`)}
        />
        <Input
          type="text"
          label="Last name"
          value={member.lastName}
          onChange={R.partial(onFieldChange, [
            ['committeeMembers', index, 'lastName'],
          ])}
          error={firstError(errors, `committeeMember[${index}].lastName`)}
        />
      </div>
    ),
    members,
  ),
);

const DissertationInfoStepPanel = ({
  degrees,
  departments,
  errors,
  fields,
  licenseTypes,
  loading,
  onAddCommitteeMember,
  onBlur,
  onCancel,
  onDegreeChanged,
  onDepartmentChanged,
  onFieldChange,
  onLanguageChanged,
  onLicenseTypeChanged,
  onNext,
}) =>
  loading ? (
    <LoadingPanel />
  ) : (
    <div className={styles.stepPanel}>
      <h1>Dissertation / Thesis Info</h1>

      <div className={styles.row}>
        <DissertationInfoLanguageAutocomplete />
      </div>

      <div className={styles.row}>
        <Input
          type="text"
          label="Title"
          value={fields.title}
          onChange={R.partial(onFieldChange, [['title']])}
          error={firstError(errors, 'title')}
          onBlur={R.partial(onBlur, ['title', fields.title])}
        />
      </div>

      <div className={styles.row}>
        <Input
          type="text"
          label="Subtitle (optional)"
          value={fields.subtitle}
          onChange={R.partial(onFieldChange, [['subtitle']])}
          error={firstError(errors, 'subtitle')}
          onBlur={R.partial(onBlur, ['subtitle', fields.subtitle])}
        />
      </div>

      <div className={styles.row}>
        <Input
          type="text"
          label="DOI (optional)"
          value={fields.doi}
          onChange={R.partial(onFieldChange, [['doi']])}
          error={firstError(errors, 'doi')}
          onBlur={R.partial(onBlur, ['doi', fields.doi])}
        />
      </div>

      <div className={styles.row}>
        <Input
          type="text"
          label="Keywords (optional)"
          hint="Comma separated list"
          multiline
          maxLength={1024}
          value={fields.keywords}
          onChange={R.partial(onFieldChange, [['keywords']])}
          error={firstError(errors, 'keywords')}
          onBlur={R.partial(onBlur, ['keywords', fields.keywords])}
        />
      </div>

      <div className={styles.row}>
        <Input
          type="text"
          label="Abstract"
          multiline
          rows={4}
          value={fields.abstract}
          onChange={R.partial(onFieldChange, [['abstract']])}
          error={firstError(errors, 'abstract')}
          onBlur={R.partial(onBlur, ['abstract', fields.abstract])}
        />
      </div>

      <h2>License I grant to the end user</h2>
      <Dropdown
        className={styles.licenseType}
        auto
        label="License Type"
        source={mapLicenseTypes(licenseTypes)}
        theme={DropdownTheme}
        value={fields.licenseType ? fields.licenseType.licenseTypeId : void 0}
        onChange={onLicenseTypeChanged}
        error={firstError(errors, 'licenseType.licenseTypeId')}
      />

      <h2>Institutional Info</h2>

      <div className={styles.row}>
        <Input
          className={styles.manuscriptCompletionYear}
          type="text"
          label="Year manuscript completed"
          value={fields.manuscriptCompletionYear}
          onChange={R.partial(onFieldChange, [['manuscriptCompletionYear']])}
          error={firstError(errors, 'manuscriptCompletionYear')}
          onBlur={R.partial(onBlur, [
            'manuscriptCompletionYear',
            fields.manuscriptCompletionYear,
          ])}
        />
      </div>

      <div className={styles.row}>
        <Input
          type="text"
          label="Institution/University"
          value={fields.institutionName}
          onChange={R.partial(onFieldChange, [['institutionName']])}
          error={firstError(errors, 'institutionName')}
          onBlur={R.partial(onBlur, [
            'institutionName',
            fields.institutionName,
          ])}
        />
      </div>

      <div className={styles.row}>
        <Dropdown
          className={styles.degreeAwarded}
          auto
          label="Degree awarded"
          source={mapDegrees(degrees)}
          theme={DropdownTheme}
          value={R.path(['degreeAwarded', 'degreeId'], fields)}
          onChange={onDegreeChanged}
          error={firstError(errors, 'degreeAwarded.degreeId')}
          // onBlur={R.partial(onBlur, ['degreeAwarded', fields.degreeAwarded])}
        />
      </div>

      <div className={styles.row}>
        <Dropdown
          className={styles.department}
          auto
          label="Department"
          source={mapDepartments(departments)}
          theme={DropdownTheme}
          value={R.path(['department', 'departmentId'], fields)}
          onChange={onDepartmentChanged}
          error={firstError(errors, 'department.departmentId')}
          // onBlur={R.partial(onBlur, ['department', fields.department])}
        />
      </div>

      <h2>Categories</h2>

      <DissertationInfoCategories />

      <h2>Advisors and Committee Members</h2>

      <h3>Advisor (optional when a supervisor is entered)</h3>

      <div className={styles.row}>
        <Input
          type="text"
          label="First name"
          value={fields.advisor.firstName}
          onChange={R.partial(onFieldChange, [['advisor', 'firstName']])}
          error={firstError(errors, 'advisor.firstName')}
          onBlur={R.partial(onBlur, [
            'advisor',
            'firstName',
            fields.advisor.firstName,
          ])}
        />
        <Input
          type="text"
          label="Last name"
          value={fields.advisor.lastName}
          onChange={R.partial(onFieldChange, [['advisor', 'lastName']])}
          error={firstError(errors, 'advisor.lastName')}
          onBlur={R.partial(onBlur, [
            'advisor',
            'lastName',
            fields.advisor.lastName,
          ])}
        />
      </div>

      <h3>Supervisor (optional when an advisor is entered)</h3>

      <div className={styles.row}>
        <Input
          type="text"
          label="First name"
          value={fields.supervisor.firstName}
          onChange={R.partial(onFieldChange, [['supervisor', 'firstName']])}
          error={firstError(errors, 'supervisor.firstName')}
          onBlur={R.partial(onBlur, [
            'supervisor',
            'firstName',
            fields.supervisor.firstName,
          ])}
        />
        <Input
          type="text"
          label="Last name"
          value={fields.supervisor.lastName}
          onChange={R.partial(onFieldChange, [['supervisor', 'lastName']])}
          error={firstError(errors, 'supervisor.lastName')}
          onBlur={R.partial(onBlur, [
            'supervisor',
            'lastName',
            fields.supervisor.lastName,
          ])}
        />
      </div>

      <h3>Committee chair (optional)</h3>

      <div className={styles.row}>
        <Input
          type="text"
          label="First name"
          value={fields.committeeChair.firstName}
          onChange={R.partial(onFieldChange, [['committeeChair', 'firstName']])}
          error={firstError(errors, 'committeeChair.firstName')}
          onBlur={R.partial(onBlur, [
            'committeeChair',
            'firstName',
            fields.committeeChair.firstName,
          ])}
        />
        <Input
          type="text"
          label="Last name"
          value={fields.committeeChair.lastName}
          onChange={R.partial(onFieldChange, [['committeeChair', 'lastName']])}
          error={firstError(errors, 'committeeChair.lastName')}
          onBlur={R.partial(onBlur, [
            'committeeChair',
            'lastName',
            fields.committeeChair.lastName,
          ])}
        />
      </div>

      <h3>Committee members (optional)</h3>

      {createCommitteeMemberRows(
        onFieldChange,
        errors,
        fields.committeeMembers,
      )}

      {fields.committeeMembers.length < MAX_COMMITTEE_MEMBERS && (
        <div className={styles.rowRight}>
          <Button
            className={styles.addAnotherMemberButton}
            label="Add another member"
            raised
            primary
            onClick={onAddCommitteeMember}
          />
        </div>
      )}

      <StepPanelButtonBar onNext={onNext} onCancel={onCancel} />
    </div>
  );

const mapState = state =>
  R.pipe(
    getStep(steps.DISSERTATION_INFO),
    R.applySpec({
      degrees: getDegrees,
      departments: getDepartments,
      errors: getErrors,
      fields: getFields,
      loading: getLoading,
      licenseTypes: getLicenseTypes,
    }),
  );

const mapDispatch = dispatch => ({
  onAddCommitteeMember: () => dispatch({ type: ADD_COMMITTEE_MEMBER }),
  onBlur: (field, value) => dispatch({ type: FIELD_BLURRED, field, value }),
  onCancel: () => dispatch({ type: SHOW_CANCEL_SUBMISSION_DIALOG, show: true }),
  onDegreeChanged: (degreeId, event) =>
    dispatch({ type: DEGREE_CHANGED, degreeId }),
  onDepartmentChanged: (departmentId, event) =>
    dispatch({ type: DEPARTMENT_CHANGED, departmentId }),
  onFieldChange: (path, value) =>
    dispatch({ type: FIELD_CHANGED, path, value }),
  onLanguageChanged: (code, event) =>
    dispatch({ type: LANGUAGE_CHANGED, code }),
  onLicenseTypeChanged: (licenseTypeId, event) =>
    dispatch({ type: LICENSE_TYPE_CHANGED, licenseTypeId }),
  onNext: () => dispatch({ type: COMPLETE_STEP }),
});

export default connect(mapState, mapDispatch)(DissertationInfoStepPanel);



// WEBPACK FOOTER //
// ./src/dissertation-info/components/DissertationInfo.js